"use client"

import React, { useEffect, useState } from "react"
import Link from "next/link"
import Navbar from "@/components/navbar"
import SecondaryServicesNav from "@/components/SecondaryServicesNav"
import HeroSearch from "@/components/HeroSearch"
import SectionTitle from "@/components/SectionTitle"
import PdfPagedCatalogViewer from "@/components/PdfPagedCatalogViewer"
import WhyChooseUsCards from "@/components/WhyChooseUsCards"
import FAQList from "@/components/FAQList"
import PromotionsCard from "@/components/PromotionsCard"
import PackageCard from "@/components/PackageCard"
import HTMLRenderer from "@/components/ui/html-renderer"
import { useI18n } from "@/context/i18n"
import { pickLocalized } from "@/lib/i18nUtils"
import type { RelatedPackage, Reason as ReasonModel, Step as StepModel, FAQ as FAQModel } from "@/lib/productsStore"
import JoinChat from "./JoinChat"
import { ShieldCheck, Users, Globe, Calendar, Award, CheckCircle } from "lucide-react"
import CTASection from "@/app/components/cta-section"
import DirectorSpotlightSection from "@/app/servicios/_components/DirectorSpotlightSection"
import AccreditationsSection from "@/app/servicios/_components/AccreditationsSection"
import PhotoStripSection, { type PhotoStripItem } from "@/app/servicios/_components/PhotoStripSection"
import AdvisorCarouselSection, { type AdvisorCarouselItem } from "@/app/servicios/_components/AdvisorCarouselSection"

type ServiceKey = "lets-study" | "lets-travel" | "lets-inmigrate" | "participaciones" | "others"
type HeroVariant = "travel" | "study" | "home" | "work"

type ReasonEx = ReasonModel & { Icon?: any }
type StepEx = StepModel & { Icon?: any }

interface ServiceTemplateProps {
  heroVariant: HeroVariant
  selectedService: ServiceKey
  title: string
  title_en?: string
  title_fr?: string
  subtitle?: string
  subtitle_en?: string
  subtitle_fr?: string
  description: string
  description_en?: string
  description_fr?: string
  heroImages?: string[]
  formUrl?: string
  reasons: ReasonEx[]
  stepsTitle?: string
  stepsTitle_en?: string
  stepsTitle_fr?: string
  steps: StepEx[]
  faqs: FAQModel[]
  relatedPackages?: RelatedPackage[]
  slug?: string
  tutorialTitle?: string
  tutorialDescription?: string
  tutorialOpenUrl?: string
  tutorialPdfUrl?: string
  catalogTitle?: string
  catalogDescription?: string
  catalogHref?: string
  catalogCtaLabel?: string
  catalogItems?: Array<{
    title: string
    image: string
    tag?: string
    description?: string
    href?: string
  }>
  directorSpotlightTitle?: string
  directorSpotlightEyebrow?: string
  directorSpotlightName?: string
  directorSpotlightRole?: string
  directorSpotlightImage?: string
  directorSpotlightDescription?: string
  directorSpotlightHighlights?: string[]
  directorSpotlightNoteTitle?: string
  directorSpotlightNoteDescription?: string
  accreditationTitle?: string
  accreditationSubtitle?: string
  accreditationLogos?: string[]
  photoStripTitle?: string
  photoStripItems?: PhotoStripItem[]
  advisorCarouselTitle?: string
  advisorCarouselDescription?: string
  advisorCarouselItems?: AdvisorCarouselItem[]
}

type LocalizedRelatedPackage = RelatedPackage & {
  title: string
  subtitle?: string
  description?: string
  travelDates?: string
  pricePrefix?: string
  priceSuffix?: string
  taxesLabel?: string
  visiting?: any[]
}

function RelatedPackagesSection({
  packages,
  slug,
  enrollLabel,
  title,
  subtitle,
}: {
  packages: LocalizedRelatedPackage[]
  slug?: string
  enrollLabel: string
  title: string
  subtitle: string
}) {
  const [isClient, setIsClient] = useState(false)
  const [isMobile, setIsMobile] = useState(false)

  useEffect(() => {
    setIsClient(true)
    if (typeof window === 'undefined') return

    const mq = window.matchMedia('(max-width: 767px)')
    const onChange = () => setIsMobile(mq.matches)

    onChange()

    try {
      mq.addEventListener('change', onChange)
      return () => mq.removeEventListener('change', onChange)
    } catch {
      mq.addListener(onChange)
      return () => {
        try { mq.removeListener(onChange) } catch {}
      }
    }
  }, [])

  const useCarousel = isClient ? (isMobile ? packages.length > 1 : packages.length > 2) : false

  return (
    <section className="py-12 md:py-16 bg-gray-50">
      <div className="container mx-auto max-w-[1400px] px-4 md:px-8">
        <div className="mb-8 text-center">
          <SectionTitle text={title} variant="light" className="text-4xl md:text-5xl mb-2" />
          <p className="text-gray-600" style={{ fontFamily: 'Montserrat, sans-serif' }}>{subtitle}</p>
        </div>

        {useCarousel ? (
          <div className="relative">
            <div className="overflow-x-auto scroll-smooth py-2">
              <div className="flex items-stretch" style={{ padding: '0 0px' }}>
                {packages.map((p, idx) => (
                  <div key={idx} className="flex-none basis-[clamp(20rem,40vw,40rem)] snap-start">
                    <PackageCard
                      title={p.title}
                      subtitle={p.subtitle}
                      description={p.description}
                      image={p.image}
                      days={p.days}
                      nights={p.nights}
                      countries={p.countries}
                      cities={p.cities}
                      priceFinal={p.priceFinal}
                      currency={p.currency}
                      pricePrefix={p.pricePrefix}
                      priceSuffix={p.priceSuffix}
                      taxesLabel={p.taxesLabel}
                      includeFlight={p.includeFlight}
                      airlineLogo={p.airlineLogo}
                      itineraryPdfUrl={p.itineraryPdfUrl}
                      href={p.href}
                      whatsappHref={p.whatsappHref}
                      availableDeparturesLabel={slug === 'grupos-educativos' ? enrollLabel : undefined}
                      visiting={p.visiting || []}
                      isVisas={slug === 'visas'}
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 items-stretch gap-4 sm:grid-cols-2 lg:grid-cols-2">
            {packages.map((p, idx) => (
              <PackageCard
                key={idx}
                title={p.title}
                subtitle={p.subtitle}
                description={p.description}
                image={p.image}
                days={p.days}
                nights={p.nights}
                countries={p.countries}
                cities={p.cities}
                priceFinal={p.priceFinal}
                currency={p.currency}
                pricePrefix={p.pricePrefix}
                priceSuffix={p.priceSuffix}
                taxesLabel={p.taxesLabel}
                includeFlight={p.includeFlight}
                airlineLogo={p.airlineLogo}
                itineraryPdfUrl={p.itineraryPdfUrl}
                href={p.href}
                whatsappHref={p.whatsappHref}
                availableDeparturesLabel={slug === 'grupos-educativos' ? enrollLabel : undefined}
                visiting={p.visiting || []}
                isVisas={slug === 'visas'}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  )
}

export default function ServiceTemplate(props: ServiceTemplateProps) {
  const { t, lang } = useI18n()
  const {
    heroVariant,
    selectedService,
    title, title_en, title_fr,
    subtitle, subtitle_en, subtitle_fr,
    description, description_en, description_fr,
    heroImages,
    formUrl,
    reasons,
    stepsTitle, stepsTitle_en, stepsTitle_fr,
    steps,
    faqs,
    relatedPackages,
    slug,
    tutorialTitle,
    tutorialDescription,
    tutorialOpenUrl,
    tutorialPdfUrl,
    catalogTitle,
    catalogDescription,
    catalogHref,
    catalogCtaLabel,
    catalogItems,
    directorSpotlightTitle,
    directorSpotlightEyebrow,
    directorSpotlightName,
    directorSpotlightRole,
    directorSpotlightImage,
    directorSpotlightDescription,
    directorSpotlightHighlights,
    directorSpotlightNoteTitle,
    directorSpotlightNoteDescription,
    accreditationTitle,
    accreditationSubtitle,
    accreditationLogos,
    photoStripTitle,
    photoStripItems,
    advisorCarouselTitle,
    advisorCarouselDescription,
    advisorCarouselItems,
  } = props

  const heroSelectedKey = (selectedService === "participaciones" ? "lets-participaciones" : selectedService) as any

  const deriveFormUrl = (s?: string) => {
    if (!s) return undefined
    const trimmed = s.trim()
    if (/^https?:\/\//i.test(trimmed)) {
      try {
        const u = new URL(trimmed)
        if (/^\/jsform\//i.test(u.pathname)) {
          const parts = u.pathname.split('/').filter(Boolean)
          const id = parts[1]
          if (id) return `${u.origin}/${id}`
        }
        return u.toString()
      } catch {
        return trimmed
      }
    }
    const m = trimmed.match(/src\s*=\s*"([^"]+)"|src\s*=\s*'([^']+)'/i)
    return m ? (m[1] || m[2]) : undefined
  }
  const effectiveFormUrl = deriveFormUrl(formUrl)
  // Use NEXT_PUBLIC_ var directly to avoid SSR/Client mismatch during hydration
  const envForm = process.env.NEXT_PUBLIC_JOTFORM_URL || ''
  const normalizedFormUrl = effectiveFormUrl || envForm

  const chooseIcon = (ttl?: string, Icon?: any) => {
    if (Icon) return Icon
    const s = (ttl || '').toLowerCase()
    if (s.includes('segur') || s.includes('seguridad')) return ShieldCheck
    if (s.includes('equipo') || s.includes('confianza') || s.includes('team')) return Users
    if (s.includes('experien') || s.includes('internac') || s.includes('integr')) return Globe
    if (s.includes('calend') || s.includes('tiempo') || s.includes('toler')) return Calendar
    if (s.includes('calidad') || s.includes('garant')) return Award
    return CheckCircle
  }

  const enrollLabel = t('cta_enroll_now') || 'Inscríbete ahora'
  const registerFormTitle = t('form_register_title') || 'Formulario de registro'

  const L = {
    title: pickLocalized(lang, title, title_en, title_fr),
    subtitle: pickLocalized(lang, subtitle, subtitle_en, subtitle_fr),
    description: pickLocalized(lang, description, description_en, description_fr),
    stepsTitle: pickLocalized(lang, stepsTitle, stepsTitle_en, stepsTitle_fr),
    reasons: (reasons || []).map((r) => ({
      ...r,
      title: pickLocalized(lang, r.title, (r as any).title_en, (r as any).title_fr),
      desc: pickLocalized(lang, r.desc, (r as any).desc_en, (r as any).desc_fr),
    })),
    steps: (steps || []).map((s) => ({
      ...s,
      title: pickLocalized(lang, s.title, (s as any).title_en, (s as any).title_fr),
      desc: pickLocalized(lang, s.desc, (s as any).desc_en, (s as any).desc_fr),
    })),
    faqs: (faqs || []).map((f) => ({
      ...f,
      q: pickLocalized(lang, f.q, (f as any).q_en, (f as any).q_fr),
      a: pickLocalized(lang, f.a, (f as any).a_en, (f as any).a_fr),
      category: (f as any).category,
    })),
    relatedPackages: (relatedPackages || []).map((rp) => ({
      ...rp,
      title: pickLocalized(lang, rp.title, (rp as any).title_en, (rp as any).title_fr),
      subtitle: pickLocalized(lang, (rp as any).subtitle, (rp as any).subtitle_en, (rp as any).subtitle_fr),
      description: pickLocalized(lang, (rp as any).description, (rp as any).description_en, (rp as any).description_fr),
      travelDates: pickLocalized(lang, (rp as any).travelDates, (rp as any).travelDates_en, (rp as any).travelDates_fr),
      pricePrefix: pickLocalized(lang, (rp as any).pricePrefix, (rp as any).pricePrefix_en, (rp as any).pricePrefix_fr),
      priceSuffix: pickLocalized(lang, (rp as any).priceSuffix, (rp as any).priceSuffix_en, (rp as any).priceSuffix_fr),
      taxesLabel: pickLocalized(lang, (rp as any).taxesLabel, (rp as any).taxesLabel_en, (rp as any).taxesLabel_fr),
      visiting: (rp as any).visiting || [],
    })),
  }

  const groupedFaqs: Record<string, { q: string; a: string }[]> = {}
  ;(L.faqs || []).forEach((f) => {
    const cat = (f as any).category || 'General'
    if (!groupedFaqs[cat]) groupedFaqs[cat] = []
    groupedFaqs[cat].push({ q: f.q, a: f.a })
  })

  return (
    <div className="min-h-screen bg-white relative overflow-x-hidden" style={{ fontFamily: 'Barlow, sans-serif' }}>
      <Navbar />

  <HeroSearch
    variant={heroVariant}
    title={L.title}
    subtitle={L.subtitle}
    selectedKey={heroSelectedKey}
    imagesOverride={heroImages}
    formUrl={normalizedFormUrl}
  />
      <SecondaryServicesNav />

      {L.description && (
        <section className="bg-white py-12 md:py-16">
          <div className="container mx-auto px-4 md:px-8 max-w-3xl">
            <div className="text-zinc-600 text-base md:text-lg leading-relaxed space-y-4 whitespace-pre-line" style={{ fontFamily: 'Montserrat, sans-serif' }}>
              <HTMLRenderer content={L.description} />
            </div>
          </div>
        </section>
      )}

      {catalogHref && (
        <section className="bg-white px-4 pb-20 md:px-8">
          <div className="mx-auto max-w-[1400px]">
            <div className="relative overflow-hidden rounded-[36px] border border-zinc-200 bg-[linear-gradient(180deg,_#fffdfa_0%,_#f8f5f1_100%)] p-6 shadow-[0_26px_90px_rgba(15,23,42,0.08)] md:p-12">
              <div className="absolute -left-20 top-10 h-48 w-48 rounded-full bg-[#fb0009]/8 blur-3xl" aria-hidden="true" />
              <div className="relative z-10 mx-auto max-w-3xl text-center">
                <SectionTitle text={catalogTitle || "Conoce nuestro catalogo"} variant="light" className="text-4xl md:text-5xl" />
                {catalogDescription && (
                  <p className="mt-5 text-sm leading-7 text-zinc-600 md:text-base" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                    {catalogDescription}
                  </p>
                )}
              </div>

              {(catalogItems && catalogItems.length > 0) ? (
                <>
                  <div className="relative z-10 mt-10 grid gap-8 md:grid-cols-2">
                    {catalogItems.map((item, idx) => {
                      const tiltClassName =
                        idx % 2 === 0
                          ? 'md:rotate-[-1.5deg] hover:rotate-0'
                          : 'md:rotate-[1.5deg] hover:rotate-0'

                      return (
                        <Link
                          key={item.title}
                          href={item.href || catalogHref}
                          className={`group overflow-hidden rounded-[10px] border border-zinc-200/80 bg-white p-4 pb-5 shadow-[0_18px_45px_rgba(15,23,42,0.12)] transition duration-300 hover:-translate-y-1 hover:shadow-[0_24px_55px_rgba(15,23,42,0.18)] ${tiltClassName}`}
                        >
                          <div className="relative h-[320px] overflow-hidden rounded-[4px] bg-zinc-100 md:h-[360px]">
                            <div
                              className="absolute inset-0 bg-cover bg-center transition duration-500 group-hover:scale-105"
                              style={{ backgroundImage: `linear-gradient(180deg, rgba(17,24,39,0.04) 0%, rgba(17,24,39,0.20) 100%), url('${item.image}')` }}
                            />
                            <div className="absolute left-5 top-5">
                              <span
                                className="inline-flex rounded-full border border-white/50 bg-white/80 px-3 py-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-zinc-800 backdrop-blur-sm"
                                style={{ fontFamily: 'Montserrat, sans-serif' }}
                              >
                                {item.tag || 'Destino recomendado'}
                              </span>
                            </div>
                          </div>

                          <div className="px-2 pt-5">
                            <h3 className="text-3xl font-bold text-zinc-950" style={{ fontFamily: 'DIN, sans-serif' }}>
                                {item.title}
                            </h3>
                            {item.description && (
                              <p className="mt-3 min-h-[72px] text-sm leading-6 text-zinc-600" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                                {item.description}
                              </p>
                            )}
                            <div className="mt-6 inline-flex h-11 w-11 items-center justify-center rounded-full border border-[#fb0009]/20 bg-[#fb0009]/8 text-[#fb0009] transition group-hover:border-zinc-950/20 group-hover:bg-zinc-950 group-hover:text-white">
                              <span aria-hidden="true" className="text-lg leading-none">&rarr;</span>
                            </div>
                          </div>
                        </Link>
                      )
                    })}
                  </div>
                </>
              ) : (
                <div className="relative z-10 mt-8">
                  <Link
                    href={catalogHref}
                    className="inline-flex items-center rounded-full bg-[#fb0009] px-7 py-3 text-sm font-semibold text-white transition hover:bg-black"
                    style={{ fontFamily: 'Montserrat, sans-serif' }}
                  >
                    {catalogCtaLabel || "Ver catalogo"}
                  </Link>
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {accreditationLogos && accreditationLogos.length > 0 && (
        <AccreditationsSection
          title={accreditationTitle}
          subtitle={accreditationSubtitle}
          logos={accreditationLogos}
        />
      )}

      {directorSpotlightImage && directorSpotlightName && directorSpotlightDescription && (
        <DirectorSpotlightSection
          title={directorSpotlightTitle || "Conoce a nuestra directora de programas"}
          eyebrow={directorSpotlightEyebrow || "Conoce a"}
          name={directorSpotlightName}
          role={directorSpotlightRole || "Directora de programas"}
          image={directorSpotlightImage}
          description={directorSpotlightDescription}
          highlights={directorSpotlightHighlights || []}
          noteTitle={directorSpotlightNoteTitle}
          noteDescription={directorSpotlightNoteDescription}
        />
      )}

      {photoStripItems && photoStripItems.length > 0 && (
        <PhotoStripSection
          title={photoStripTitle}
          items={photoStripItems}
        />
      )}

      {advisorCarouselItems && advisorCarouselItems.length > 0 && (
        <AdvisorCarouselSection
          title={advisorCarouselTitle || "Conoce al equipo"}
          subtitle={advisorCarouselDescription || "Una tarjeta a la vez para descubrir quién te acompaña en la planeación, seguimiento y administración de tus grupos educativos."}
          items={advisorCarouselItems}
        />
      )}

      {(L.relatedPackages && L.relatedPackages.length > 0) && (
        <RelatedPackagesSection
          packages={L.relatedPackages}
          slug={slug}
          enrollLabel={enrollLabel}
          title={slug === 'grupos-educativos' ? enrollLabel : t('services_relevantes_title')}
          subtitle={t('services_packages_similar')}
        />
      )}

      <section className="py-12 md:py-16 bg-white">
        <div className="container mx-auto px-4 md:px-8 max-w-6xl">
          <div className="text-center mb-10">
            <SectionTitle text={t('services_why_choose_title')} variant="light" className="text-4xl md:text-5xl" />
          </div>
          {(() => {
            const reasonsCols = (L.reasons?.length || 0) <= 3
              ? 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6'
              : 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6'
            return (
            <WhyChooseUsCards
              items={L.reasons.map(r => ({
                icon: chooseIcon(r.title, (r as any).Icon) as any,
                title: r.title,
                description: r.desc,
              }))}
              variant="simple"
              gridClassName={reasonsCols}
            />
            )
          })()}
        </div>
      </section>

      <section className="py-12 md:py-16 bg-white">
        <div className="container mx-auto px-4 md:px-8 max-w-6xl">
          <div className="text-center mb-12">
            <SectionTitle text={L.stepsTitle || t('services_process_title')} variant="light" className="text-4xl md:text-5xl" />
          </div>
          <ul className={`grid gap-8 ${ (L.steps?.length || 0) <= 3 ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3' : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-4' }`}>
            {L.steps.map((s, idx) => (
              <li key={idx} className="relative bg-white rounded-xl shadow-md p-8 flex flex-col items-center text-center transition-transform duration-300 hover:scale-105 group overflow-hidden">
                <div className="absolute top-0 left-0 w-full h-1 bg-[#fb0009] origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-200" aria-hidden="true" />
                <div className="h-20 w-20 mb-6 rounded-full bg-[#fb0009] text-white flex items-center justify-center">
                  <span className="text-2xl font-extrabold select-none">{idx + 1}</span>
                </div>
                <div>
                  <h4 className="font-bold text-xl mb-2" style={{ fontFamily: 'DIN, sans-serif' }}>
                    {s.title}
                  </h4>
                  {s.desc && (
                    <div className="text-gray-600" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                      <HTMLRenderer content={s.desc} />
                    </div>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {(tutorialPdfUrl || tutorialOpenUrl) && (
        <section className="bg-white">
          <div className="mx-auto max-w-[1500px] px-2 py-10 text-center md:px-5">
            {tutorialPdfUrl ? (
              <PdfPagedCatalogViewer
                pdfUrl={tutorialPdfUrl}
                errorMessage="No se pudo cargar el PDF desde Google Drive. Verifica permisos del archivo o subelo a public/pdf/catalogo-campamentos.pdf"
                title={tutorialTitle || 'Tutorial PDF'}
                description={tutorialDescription}
              />
            ) : (
              <div className="mx-auto max-w-3xl rounded-[32px] border border-zinc-200 bg-zinc-50 px-6 py-10 shadow-[0_18px_60px_rgba(15,23,42,0.06)] md:px-10 md:py-12">
                {tutorialTitle && (
                  <div className="flex justify-center">
                    <SectionTitle text={tutorialTitle} variant="light" className="text-4xl md:text-5xl" />
                  </div>
                )}
                {tutorialDescription && (
                  <p className="mx-auto mt-4 max-w-2xl text-sm leading-7 text-zinc-600 md:text-base">
                    {tutorialDescription}
                  </p>
                )}
                {tutorialOpenUrl && (
                  <div className="mt-8">
                    <Link
                      href={tutorialOpenUrl}
                      className="inline-flex items-center rounded-full bg-[#fb0009] px-7 py-3 text-sm font-semibold text-white transition hover:bg-black"
                    >
                      Abrir tutorial
                    </Link>
                  </div>
                )}
              </div>
            )}
          </div>
        </section>
      )}

      <FAQList grouped={groupedFaqs} />
      <JoinChat position="right" buttonDelay={1} />
      {/* CTA para servicios: reemplazar botones por iframe configurable desde admin */}
      <section className="bg-white pt-16 pb-20">
        <div className="container mx-auto px-4 md:px-8 max-w-5xl">
          {normalizedFormUrl && normalizedFormUrl.trim().length > 0 ? (
            <div className="w-full h-[70vh] rounded-xl overflow-hidden shadow">
              <iframe
                src={normalizedFormUrl}
                title={registerFormTitle}
                className="w-full h-full border-0"
                allow="camera; microphone; clipboard-read; clipboard-write"
              />
            </div>
          ) : (
            <CTASection />
          )}
        </div>
      </section>
    </div>
  )
}
